'use client';

import {useState, useEffect, useContext, useCallback, useRef} from 'react';
import Box from '@mui/material/Box';
import DataContext from '@/contexts/DataContext';
import { fetchExprData, extractTrackedParams, fetchAPIData, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { extractParam } from '@/lib/dataHandlers/fetch-view-params';
import { syncLastUsedParams } from '@/lib/dataHandlers/sync-last-used-params';
import { useComponentLogger } from '@/lib/component-logger';

import { InfoTooltip } from '../typography/info-tooltip';
import Stack from '@mui/material/Stack';
import { textColors, neonPurple } from '@/styles/theme/colors';
import Typography from '@mui/material/Typography';
import {APIProvider, Map, useMap, AdvancedMarker, Pin} from '@vis.gl/react-google-maps';
import {MarkerClusterer} from '@googlemaps/markerclusterer';


/**
 * Displays a an image
 * @param {} param0 
 * @returns 
 */
export function GoogleMap({ config={} }) {
    const logger = useComponentLogger("Map");
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const { currentProject, currentView, selectedViewParams, setSelectedViewParams, changedParams, setChangedParams } = useContext(DataContext);
    const [markers, setMarkers] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState(0);
    const [isIntervalStarted, setIsIntervalStarted] = useState(false);
    const intervalID = useRef(null); // need this to clear the interval on unmount
    const mapRef = useRef(null);  // track map changes to avoid unecessary re-renders
    const trackedParamValsRef = useRef(null);
    const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr


    useEffect(() => {
        let trackedParams = extractTrackedParams(config.dataSource);
        logger.debug('map config', config, currentView, selectedViewParams, trackedParams);
        let params = {};
        for (const param of trackedParams) {
            if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
        }

        logger.debug('tracked params', params);
        trackedParamValsRef.current = params;
        trackedParamNamesRef.current = trackedParams;
    
        if (selectedViewParams.map) mapRef.current = selectedViewParams.map.value ?? null;
        fetchContent().catch(() => {});
    }, []);

    const fetchContent = async () => {
        if (config.dataSource === null || config.dataSource === undefined) return;

        const dataSource = config.dataSource;

        if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
            logger.debug('map api data', data.rows, config);

            if (data?.rows && data.rows.length > 0) {
                let apiConfig = data.rows[0];
                let parsedMarkers = data.rows.map((row) => { return {...row, paramName: config?.paramName || null }})
                setMarkers(parsedMarkers);
                logger.debug('parsed markers', parsedMarkers, config);

                if (apiConfig.interval && apiConfig.interval.val) setRefreshInterval(apiConfig.interval.val * 1000);
                
            }
        }

    }

    useEffect(() => {
        let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
        logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch, selectedViewParams, trackedParamValsRef);
        if (shouldReFetch.changed) {
            trackedParamValsRef.current = shouldReFetch.paramRef;
            fetchContent();
        } 

        // make sure to clear the content if any of the tracked params were removed
        // common use case: when a parent param changes, it clears the child param
        for (const param of trackedParamNamesRef.current) { 
            if (!selectedViewParams[param]) {
                setMarkers([]);
                return;
            }
        }
    }, [changedParams]);

    useEffect(() => {
        // only start the interval the first time refreshInterval is set
        if (refreshInterval > 0 && !isIntervalStarted) {
            intervalID.current = setInterval(fetchContent, refreshInterval); // save interval id so we can clear it later
            setIsIntervalStarted(true); 
            logger.debug('interval started');

            // cleanup func, runs on unmount
            return () => {
                clearInterval(intervalID.current);
                setIsIntervalStarted(false); 
                logger.debug('interval cleared')
            };
        }
    }, [refreshInterval]);

    return (
        <APIProvider apiKey={apiKey}>
            <Box padding={0}>
                <Map
                    // style={{width: '100vw', height: '60vh'}}
                    // style={{width: '90vw', height: '60vh', padding: 0, margin: 0}}
                    // style={{width: '100%', height: '100%'}}
                    style={{height: '60vh'}}
                    defaultZoom={13}
                    defaultCenter={{ lat: 0, lng: 0 }}
                    gestureHandling='greedy'
                    disableDefaultUI
                    mapId='da37f3254c6a6d1c' // TODO: using demo map id, change to our own later?
                >
                    {markers.length > 0 ? <MapMarkers points={markers} mapObject={config}/> : <></>}
                </Map>
            </Box>
        </APIProvider>
    );
}

export function MapMarkers({points=[], mapObject={}}){
    const logger = useComponentLogger("Map Marker");
    const {selectedViewParams, setSelectedViewParams, currentView, changedParams} = useContext(DataContext);
    const map = useMap();
    const [markers, setMarkers] = useState([]);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const paramName = mapObject.paramName ?? mapObject.name ?? 'marker';

//   const clusterer = useRef(null);

//   // Initialize MarkerClusterer, if the map has changed
//   useEffect(() => {
//     if (!map) return;
//     if (!clusterer.current) {
//       clusterer.current = new MarkerClusterer({map});
//     }
//   }, [map]);

//   // Update markers, if the markers array has changed
//   useEffect(() => {
//     clusterer.current?.clearMarkers();
//     clusterer.current?.addMarkers(Object.values(markers));
//   }, [markers]);

    const setMarkerRef = (marker, key) => {
        if (marker && markers[key]) return;
        if (!marker && !markers[key]) return;

        setMarkers(prev => {
            if (marker) {
                return {...prev, [key]: marker};
            } else {
                const newMarkers = {...prev};
                delete newMarkers[key];
                return newMarkers;
            }
            });
    };

    const handleMarkerClick = useCallback((ev, marker) => {
        logger.debug('marker clicked: ', ev.latLng.toString(), marker);
        if (!map) return;
        if(!ev.latLng) return;
        
        map.panTo(ev.latLng);
        setSelectedMarker(marker);

        let markerParam = extractParam(marker, currentView?.viewData?.parameters || []) ?? null;
        logger.debug('marker clicked extract param:', markerParam, marker, currentView);

        if (markerParam === null) return;

        let newParams = {...selectedViewParams};
        newParams[paramName] = {...markerParam, value: marker.id.val}; // params need a value attribute
        const appID = currentView.mobileAppRef?.val;
        if (appID) syncLastUsedParams(`lastUsedParams-${appID}`, newParams);
        logger.debug('set selected: marker click')
        setSelectedViewParams(newParams);

        logger.debug('marker clicked final params: ', ev.latLng.toString(), marker, newParams);
    });

    // auto fit map around markers
    // for some reason this has to be inside a nested component (e.g. MapMarkers nested in Map) to work
    useEffect(() => {
        if (map && points.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            points.forEach(point => {
                bounds.extend({lat: point.geoCoord.lat, lng: point.geoCoord.lng});
            });
            map.fitBounds(bounds);
        }
    }, [map, markers]);

    useEffect(() => {
        logger.debug("changed params", changedParams, selectedViewParams, selectedMarker);
        if (points.length === 0) return;
    
        if (selectedViewParams[paramName]) {
            let selectedMarkerValue = selectedViewParams[paramName].value ?? '';
            if (selectedMarkerValue !== selectedMarker?.id?.val) {
                let match = points.find((point) => point.id.val === selectedMarkerValue);
                logger.debug('selected change match', match)
                if (match) setSelectedMarker(match);
            }
        } else setSelectedMarker(null); // reset marker if it was removed (e.g. on parent change)
    }, [changedParams, selectedViewParams, points]);

    useEffect(() => {
        logger.debug('marker start', selectedMarker, selectedViewParams, mapObject, paramName, points)
    }, []);

    useEffect(() => {
        logger.debug('selected marker change', selectedMarker, selectedViewParams)
    }, [selectedMarker])

    return (
        <>
        {points?.map((point) => {
            const isSelected = selectedMarker?.id.val === point.id.val;

            return (
                <AdvancedMarker
                    key={point.id.val}
                    position={point.geoCoord}
                    title={point.id.dis || ""}
                    ref={marker => setMarkerRef(marker, point.id.val)}
                    clickable={true}
                    onClick={(ev) => handleMarkerClick(ev, point)}
                >
                    <Pin 
                        background={'var(--mui-palette-primary-main)'} 
                        // glyphColor={'var(--mui-palette-text-primary)'} 
                        // glyphColor={'#fff'}
                        glyphColor={isSelected ? 'var(--mui-palette-neutral-800)' : '#fff'}
                        borderColor={'transparent'}
                        scale={isSelected ? 1.55 : 1}
                    />
                </AdvancedMarker>
            )
        })}
        </>
    );
};